'use client'
import { useMemo, useState } from 'react'
import { isAndroid, isIOS, isMobile, isTablet } from 'react-device-detect'

import Shared from '@/shared'
import { useInitKakaoSdk } from '@/shared/hooks/useInitKakaoSdk'
import type { SetStateAction, Dispatch } from 'react'

interface NavigationViewModelProps {
  parkinglotName: string
  latitude: number
  longitude: number
}

interface NavigationViewModel {
  texts: object
  states: {
    toggleSpinner: boolean
    isToast: boolean
    toastText: string
  }
  setters: {
    setIsToast: Dispatch<SetStateAction<boolean>>
  }
  handlers: {
    handleTmap: () => void
    handleNaverMap: () => void
    handleKakaoNavi: () => void
  }
}

const useNavigationViewModel = ({
  parkinglotName,
  latitude,
  longitude
}: NavigationViewModelProps): NavigationViewModel => {
  useInitKakaoSdk()
  const [toggleSpinner, setToggleSpinner] = useState(false)
  const [isToast, setIsToast] = useState(false)
  const [toastText, setToastText] = useState('')

  const isSocarInApp = useMemo(() => {
    if (!window) return false
    return window.navigator.userAgent.includes('SOCAR') || window.navigator.userAgent.includes('socar')
  }, [])

  const convertWGS84ToEPSG3857 = ({ lat, lng }: { lat: number; lng: number }) => {
    const WGS84Coord = new naver.maps.LatLng(lat, lng)
    const EPSGCoord = naver.maps.TransCoord.fromLatLngToEPSG3857(WGS84Coord)

    return {
      lat: EPSGCoord.y,
      lng: EPSGCoord.x
    }
  }

  const handleNaverMap = async () => {
    const destinationLat = latitude
    const destinationLng = longitude
    const destinationName = parkinglotName

    if (isMobile || isTablet) {
      const iOSScheme = `nmap://navigation?dlat=${encodeURIComponent(destinationLat)}&dlng=${encodeURIComponent(
        destinationLng
      )}&dname=${encodeURIComponent(destinationName)}&appname=com.parkingshare.parkingshare`

      const aOSScheme = `intent://navigation?dlat=${encodeURIComponent(destinationLat)}&dlng=${encodeURIComponent(
        destinationLng
      )}&dname=${encodeURIComponent(
        destinationName
      )}&appname=com.parkingshare.parkingshare#Intent;scheme=nmap;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;package=com.nhn.android.nmap;end`

      if (isAndroid) {
        window.open(aOSScheme, '_blank')
        return
      }

      if (isIOS) {
        const socarInAppScheme = 'socar-v2://open-url/external?url='
        const clickedAt = new Date().getTime()

        if (isSocarInApp) {
          location.href = socarInAppScheme + encodeURIComponent(iOSScheme)
        } else {
          location.href = iOSScheme
        }

        setTimeout(() => {
          if (new Date().getTime() - clickedAt < 2000) {
            location.href = 'http://itunes.apple.com/app/id311867728?mt=8'
          }
        }, 1500)
        return
      }
    } else {
      try {
        const naverMapHost = 'https://map.naver.com/v5/directions'

        if (window.navigator.permissions) {
          const { state } = await window.navigator.permissions.query({ name: 'geolocation' })
          if (state === 'denied') {
            throw new Shared.Errors.GeolocationError()
          }
          setToggleSpinner(true)
        }

        const currentLocation = await Shared.Utils.getCurrentLatLng()
        const from = convertWGS84ToEPSG3857({ lat: currentLocation.lat, lng: currentLocation.lng })
        const to = convertWGS84ToEPSG3857({ lat: destinationLat, lng: destinationLng })

        const desktopScheme = `${naverMapHost}/${from.lng},${from.lat},현재위치,,ADDRESS_POI/${to.lng},${
          to.lat
        },${encodeURIComponent(destinationName)},,PLACE_POI/-/car?c=10,0,0,0,dh`

        window.open(desktopScheme, '_blank')
        return
      } catch (err: unknown) {
        if (err instanceof Shared.Errors.GeolocationError) {
          setIsToast(true)
          setToastText(err.message)
        } else {
          throw err
        }
      } finally {
        setToggleSpinner(false)
      }
    }
  }

  const handleKakaoNavi = () => {
    if (isMobile) {
      return window.Kakao.Navi.start({
        name: encodeURIComponent(parkinglotName),
        x: longitude,
        y: latitude,
        coordType: 'wgs84'
      })
    } else {
      setIsToast(true)
      setToastText('모바일 기기에서만 지원 가능합니다.')
    }
  }

  const handleTmap = () => {
    const tmapHost = 'https://api2.sktelecom.com/tmap/app/routes'
    const tmapAppKey = process.env.NEXT_PUBLIC_TMAP_API_KEY
    const tmapScheme = `${tmapHost}?appKey=${tmapAppKey}&name=${encodeURIComponent(
      parkinglotName
    )}&lon=${longitude}&lat=${latitude}&coordType=WGS84GEO`

    if (isMobile) {
      return window.open(tmapScheme, '_blank')
    } else {
      setIsToast(true)
      setToastText('모바일 기기에서만 지원 가능합니다.')
    }
  }

  return {
    texts: {},
    states: { toggleSpinner, isToast, toastText },
    setters: { setIsToast },
    handlers: { handleTmap, handleNaverMap, handleKakaoNavi }
  }
}

export default useNavigationViewModel
